import React, { useState, useEffect } from 'react';
import { Tag, FileText } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import SearchableSelect from '../ui/SearchableSelect';
import { useUpdateService } from '../../hooks/useServices';
import { SERVICE_TYPES } from './serviceTypes';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EditServiceModal = ({ isOpen, onClose, service, supplierUid }) => {
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const updateService = useUpdateService();
  const { t } = useLanguage();

  const handleTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, type: value }));
    if (errors.type) {
      setErrors((prev) => ({ ...prev, type: '' }));
    }
  };

  useEffect(() => {
    if (service) {
      setFormData({
        type: service.type || '',
        name: service.name || '',
        description: service.description || '',
      });
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = t(KEYS.SERVICE_ADD_TYPE_REQUIRED);
    }

    if (formData.name && formData.name.length > 191) {
      newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
    }

    if (formData.description && formData.description.length > 191) {
      newErrors.description = t(KEYS.COMMON_ERROR_MAX_CHARS);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const data = {
        type: formData.type,
        name: formData.name || undefined,
        description: formData.description || undefined,
      };

      const sUid = supplierUid || service?.supplier_uid;
      await updateService.mutateAsync({ supplierUid: sUid, serviceUid: service.uid, data });
      onClose();
    } catch (error) {
      if (error.code === 'VALIDATION_ERROR' && error.errors) {
        setErrors(normalizeFieldErrors(error.errors));
      } else {
        setSubmitError(error.message || t(KEYS.SERVICE_EDIT_ERROR));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setErrors({});
      setSubmitError('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t(KEYS.SERVICE_EDIT_TITLE)}
      subtitle={t(KEYS.SERVICE_EDIT_SUBTITLE)}
      size="medium"
      footer={
        <>
          <Button
            variant="outline"
            size="medium"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t(KEYS.COMMON_CANCEL)}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t(KEYS.SERVICE_EDIT_SUBMITTING) : t(KEYS.SERVICE_EDIT_SUBMIT)}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {submitError && (
          <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm">
            {submitError}
          </div>
        )}

        <div className="space-y-1">
          <label htmlFor="type" className="label-base flex items-center gap-1.5">
            <Tag className="w-4 h-4" />
            Type <span className="text-red-500">*</span>
          </label>
          <SearchableSelect
            id="type"
            options={SERVICE_TYPES}
            value={formData.type}
            onChange={handleTypeChange}
            placeholder="Select or type a service type…"
            disabled={isSubmitting}
            hasError={!!errors.type}
          />
          {errors.type && (
            <span className="text-xs text-red-500 mt-1">{errors.type}</span>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="name" className="label-base flex items-center gap-1.5">
            <FileText className="w-4 h-4" />
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`input-base ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Service name (optional)"
            maxLength={191}
            disabled={isSubmitting}
          />
          {errors.name && (
            <span className="text-xs text-red-500 mt-1">{errors.name}</span>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="description" className="label-base flex items-center gap-1.5">
            <FileText className="w-4 h-4" />
            Description
          </label>
          <input
            type="text"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className={`input-base ${errors.description ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Service description (optional)"
            maxLength={191}
            disabled={isSubmitting}
          />
          {errors.description && (
            <span className="text-xs text-red-500 mt-1">{errors.description}</span>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default EditServiceModal;
